/**
 * Timbrica — GIF Core (container-level GIF reader / retimer).
 *
 * Shared primitive. Before this file, /gif-trim and /gif-compressor each
 * carried their own `parseGifInfo()` copy that counted frames and summed
 * delays and nothing else. Neither recorded WHERE a delay lives in the byte
 * stream, so "change the speed" could only be expressed as a gifsicle
 * re-encode — which is both lossy and, as measured, frequently makes the file
 * BIGGER than the input.
 *
 * The three facts this module is built around, all verified against the 19
 * real fixtures in tests/playwright/fixtures:
 *
 *   1. A GIF frame delay is an unsigned 16-bit integer in CENTISECONDS,
 *      stored in the Graphic Control Extension. It is not a float and not
 *      milliseconds. 24 fps is 4.1667 cs and therefore INEXPRESSIBLE.
 *
 *   2. Because of (1), real encoders alternate delays — a 24 fps GIF is
 *      stored as 4,5,4,4,5,4,4,5… so the AVERAGE lands on 4.1667. Variable
 *      delays are the common case, not an exotic one: 9 of our 19 fixtures
 *      are like this. Any tool that writes one uniform delay across all
 *      frames (gifsicle `-d`, ezgif's "precise delay" mode, and until now our
 *      own /gif-trim slider) destroys that averaging and shifts the real
 *      frame rate.
 *
 *   3. Browsers clamp very short delays. A delay of 0 or 1 cs is rendered as
 *      10 cs by every mainstream engine, so the practical floor is 2 cs =
 *      50 fps. Speeding a 10 cs GIF up 8× cannot be done by shrinking delays
 *      (1.25 cs rounds to 1, which the browser then renders as 10 cs — i.e.
 *      the "8× faster" GIF plays 8× SLOWER than asked). Past the floor the
 *      only correct move is to drop frames.
 *
 * Public surface (window.GifCore):
 *
 *   GifCore.parse(arrayBuffer)          → parsed | null  (null = not a GIF)
 *   GifCore.analyze(parsed)             → human-facing facts about the file
 *   GifCore.planSpeed(parsed, opts)     → a retiming plan (pure math, no I/O)
 *   GifCore.applyRetime(buf, parsed, plan) → ArrayBuffer, byte-patched
 *   GifCore.setLoopCount(buf, parsed, n)   → ArrayBuffer
 *
 * `applyRetime` is a byte patch: it rewrites the two-byte delay field of each
 * Graphic Control Extension in place and copies everything else verbatim. The
 * pixel data is never touched, so the operation is lossless, allocation-cheap
 * and independent of image size — a 100 MB GIF retimes as fast as a 10 KB one.
 * It is only valid when the plan keeps every frame (`plan.mode === 'retime'`);
 * dropping or adding frames needs a real decode/encode round-trip, which lives
 * in the calling page because it depends on WebCodecs and gifsicle.
 */
(function (global) {
    'use strict';

    // Browsers render a delay of 0 or 1 cs as 10 cs. 2 cs (50 fps) is the
    // fastest value that survives to the screen unchanged.
    var BROWSER_FLOOR_CS = 2;
    // GIF delay field is uint16.
    var MAX_CS = 65535;

    /* ------------------------------------------------------------------ */
    /* parse                                                              */
    /* ------------------------------------------------------------------ */

    /**
     * Walk the GIF block structure and record, for every frame, both its
     * timing and the byte offset that timing lives at.
     *
     * Returns null for anything that is not a structurally sound animated or
     * still GIF — truncated files, non-GIF bytes, and files whose block walk
     * never reaches a single image descriptor. Callers rely on null to show a
     * clean "not a GIF" rejection instead of activating a workspace over a
     * file that will explode later in the encoder.
     */
    function parse(buffer) {
        var d = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
        if (d.length < 13) return null;

        var sig = String.fromCharCode(d[0], d[1], d[2], d[3], d[4], d[5]);
        if (sig !== 'GIF87a' && sig !== 'GIF89a') return null;

        var view = new DataView(d.buffer, d.byteOffset, d.byteLength);
        var width = view.getUint16(6, true);
        var height = view.getUint16(8, true);
        if (!width || !height) return null;

        var packed = d[10];
        var hasGCT = (packed >> 7) & 1;
        var gctSize = hasGCT ? (1 << ((packed & 7) + 1)) : 0;

        var pos = 13 + gctSize * 3;
        var frames = [];
        var loopOffset = -1;
        var loopCount = null;
        var loopBlock = null;   // [start, end) of the whole NETSCAPE extension, for setPlays(…, 1)
        var truncated = false;
        var pending = null; // graphic control state awaiting its image descriptor

        while (pos < d.length) {
            var block = d[pos];
            if (block === 0x3B) break; // trailer

            if (block === 0x2C) {
                if (pos + 10 > d.length) { truncated = true; break; }
                var x = view.getUint16(pos + 1, true);
                var y = view.getUint16(pos + 3, true);
                var w = view.getUint16(pos + 5, true);
                var h = view.getUint16(pos + 7, true);
                var lp = d[pos + 9];
                var hasLCT = (lp >> 7) & 1;
                var p = pos + 10;
                if (hasLCT) p += (1 << ((lp & 7) + 1)) * 3;
                p++; // LZW minimum code size
                while (p < d.length && d[p] !== 0) p += d[p] + 1;
                if (p >= d.length) { truncated = true; break; }
                p++; // sub-block terminator

                frames.push({
                    index: frames.length,
                    x: x, y: y, w: w, h: h,
                    hasLCT: !!hasLCT,
                    delayCs: pending ? pending.delayCs : 0,
                    delayOffset: pending ? pending.delayOffset : -1,
                    disposal: pending ? pending.disposal : 0,
                    transparent: pending ? pending.transparent : false,
                    // A frame is "full" when its sub-image covers the whole
                    // logical screen. Partial frames are deltas that depend on
                    // what earlier frames painted, which is why they cannot be
                    // dropped without compositing first.
                    full: (x === 0 && y === 0 && w === width && h === height),
                    blockStart: pending ? pending.gceStart : pos,
                    blockEnd: p
                });
                pending = null;
                pos = p;
            } else if (block === 0x21) {
                if (pos + 2 > d.length) { truncated = true; break; }
                var ext = d[pos + 1];
                var gceStart = pos;
                var q = pos + 2;
                if (ext === 0xF9 && q + 5 < d.length && d[q] >= 4) {
                    var pk = d[q + 1];
                    pending = {
                        gceStart: gceStart,
                        disposal: (pk >> 2) & 7,
                        transparent: !!(pk & 1),
                        delayCs: view.getUint16(q + 2, true),
                        delayOffset: q + 2
                    };
                } else if (ext === 0xFF && q < d.length && d[q] === 11) {
                    var app = '';
                    for (var k = 0; k < 11; k++) app += String.fromCharCode(d[q + 1 + k]);
                    if (app === 'NETSCAPE2.0') {
                        var sb = q + 12;
                        if (sb + 3 < d.length && d[sb] === 3 && d[sb + 1] === 1) {
                            loopOffset = sb + 2;
                            loopCount = view.getUint16(sb + 2, true);
                            loopBlock = [pos, -1];
                        }
                    }
                }
                while (q < d.length && d[q] !== 0) q += d[q] + 1;
                if (q >= d.length) { truncated = true; break; }
                q++;
                if (loopBlock && loopBlock[1] < 0 && loopBlock[0] === pos) loopBlock[1] = q;
                pos = q;
            } else {
                // Unknown byte where a block introducer was expected. The file
                // is malformed; stop rather than scanning byte-by-byte into
                // pixel data and inventing frames out of noise.
                truncated = true;
                break;
            }
        }

        if (!frames.length) return null;

        var totalCs = 0;
        for (var i = 0; i < frames.length; i++) totalCs += frames[i].delayCs;

        return {
            version: sig,
            width: width,
            height: height,
            gctSize: gctSize,
            frames: frames,
            loopCount: loopCount,
            loopOffset: loopOffset,
            loopBlock: loopBlock && loopBlock[1] > 0 ? loopBlock : null,
            totalCs: totalCs,
            truncated: truncated,
            size: d.length
        };
    }

    /* ------------------------------------------------------------------ */
    /* analyze                                                            */
    /* ------------------------------------------------------------------ */

    /**
     * Facts a user (and the UI) needs before deciding what to do. Kept
     * separate from parse() so parse stays a pure container reader.
     */
    function analyze(parsed) {
        var frames = parsed.frames;
        var n = frames.length;
        var delays = [];
        var partial = 0;
        var minCs = Infinity, maxCs = 0;
        var zeroDelay = 0;

        for (var i = 0; i < n; i++) {
            var cs = frames[i].delayCs;
            delays.push(cs);
            if (!frames[i].full) partial++;
            if (cs < minCs) minCs = cs;
            if (cs > maxCs) maxCs = cs;
            // A stored 0 or 1 is rendered as 10 cs — the file's nominal
            // duration and its real playback duration disagree.
            if (cs <= 1) zeroDelay++;
        }

        // Effective duration is what the browser actually plays, which is not
        // the sum of stored delays when any delay is below the floor.
        var effectiveCs = 0;
        for (var j = 0; j < n; j++) effectiveCs += frames[j].delayCs <= 1 ? 10 : frames[j].delayCs;

        var uniq = {};
        for (var u = 0; u < delays.length; u++) uniq[delays[u]] = 1;
        var uniqCount = Object.keys(uniq).length;

        return {
            frameCount: n,
            width: parsed.width,
            height: parsed.height,
            delays: delays,
            uniqueDelays: uniqCount,
            variableDelays: uniqCount > 1,
            minCs: minCs === Infinity ? 0 : minCs,
            maxCs: maxCs,
            partialFrames: partial,
            // True when frames are deltas painted over one another. Dropping a
            // frame from such a file corrupts everything after it unless the
            // canvas is composited first.
            needsCompositeToDrop: partial > 0,
            nominalDurationMs: parsed.totalCs * 10,
            effectiveDurationMs: effectiveCs * 10,
            subFloorFrames: zeroDelay,
            fps: parsed.totalCs ? (n / (parsed.totalCs / 100)) : 0,
            effectiveFps: effectiveCs ? (n / (effectiveCs / 100)) : 0,
            loopCount: parsed.loopCount,
            animated: n > 1
        };
    }

    /* ------------------------------------------------------------------ */
    /* planSpeed                                                          */
    /* ------------------------------------------------------------------ */

    /**
     * Work out how to reach `speed` and report honestly what it will cost.
     *
     * Two things make this more than `delay / speed`:
     *
     *   Quantisation. Delays are integers, so rounding each frame on its own
     *   both drifts the total duration and flattens the 4,5,4,4,5 pattern that
     *   encodes a non-integer frame rate. We instead carry the rounding
     *   remainder into the next frame (error diffusion), which keeps the total
     *   duration within half a centisecond of exact and reproduces the
     *   alternating pattern by construction.
     *
     *   The floor. Once a frame's target delay falls below 2 cs, delays alone
     *   cannot express the requested speed. Clamping silently under-delivers,
     *   so past that point the plan switches to resampling the timeline —
     *   keeping fewer frames, each with a legal delay, so the perceived speed
     *   is the one that was asked for.
     *
     * opts:
     *   speed        {number} >1 faster, <1 slower. Ignored if targetMs given.
     *   targetMs     {number} desired total duration in ms (overrides speed).
     *   floorCs      {number} minimum legal delay, default 2.
     *   allowDrop    {boolean} may drop frames past the floor. Default true.
     *   maxFrames    {number} cap on output frames (encoder/memory budget).
     */
    function planSpeed(parsed, opts) {
        opts = opts || {};
        var floorCs = opts.floorCs || BROWSER_FLOOR_CS;
        var allowDrop = opts.allowDrop !== false;
        var frames = parsed.frames;
        var n = frames.length;

        // A GIF whose delays are all 0/1 has no meaningful stored timing; the
        // browser is showing it at 10 cs a frame. Treat that as the baseline,
        // otherwise "2× faster" divides zero and produces nonsense.
        var base = [];
        var baseTotal = 0;
        for (var i = 0; i < n; i++) {
            var cs = frames[i].delayCs <= 1 ? 10 : frames[i].delayCs;
            base.push(cs);
            baseTotal += cs;
        }

        var speed = opts.speed || 1;
        if (opts.targetMs && baseTotal > 0) speed = (baseTotal * 10) / opts.targetMs;
        if (!isFinite(speed) || speed <= 0) speed = 1;

        var targetTotalCs = baseTotal / speed;

        // --- can we do it with delays alone? ---
        // The constraint is global, not per-frame. Every kept frame must carry
        // at least `floorCs`, so n frames can never play faster than
        // n * floorCs centiseconds no matter how the delays are distributed.
        // Testing the shortest frame instead (the obvious first guess) misses
        // the case where each frame rounds up only a little but the whole
        // animation still overshoots: 150 frames of 3 cs asked to run 2×
        // wants 250 cs and gets 300, a silent 20% miss with every frame
        // individually "legal".
        var needsDrop = targetTotalCs < n * floorCs - 0.5;

        if (!needsDrop || !allowDrop || n < 3) {
            var delays = diffuse(base, speed, floorCs);
            var achieved = sum(delays);
            return {
                mode: 'retime',
                speed: speed,
                requestedSpeed: speed,
                achievedSpeed: achieved > 0 ? baseTotal / achieved : speed,
                delays: delays,
                keep: null,               // every frame kept
                frameCount: n,
                sourceFrameCount: n,
                targetDurationMs: Math.round(targetTotalCs * 10),
                achievedDurationMs: achieved * 10,
                lossless: true,           // byte patch, pixels untouched
                floorClamped: needsDrop,  // true only when allowDrop was off
                droppedFrames: 0
            };
        }

        // --- resample the timeline ---
        // Every kept frame must carry at least `floorCs`, so the output can
        // hold at most targetTotalCs / floorCs frames. Sample the SOURCE
        // timeline at even intervals and take whichever frame is on screen at
        // each sample instant. This preserves the pacing of the original
        // (a frame held twice as long in the source is sampled twice as often)
        // instead of blindly keeping every k-th frame.
        var maxOut = Math.floor(targetTotalCs / floorCs);
        if (opts.maxFrames) maxOut = Math.min(maxOut, opts.maxFrames);
        if (maxOut < 2) maxOut = 2;
        if (maxOut > n) maxOut = n;

        // Cumulative start time of each source frame, in source centiseconds.
        var starts = [];
        var acc = 0;
        for (var s = 0; s < n; s++) { starts.push(acc); acc += base[s]; }

        var keep = [];
        var step = baseTotal / maxOut;
        var lastIdx = -1;
        for (var k = 0; k < maxOut; k++) {
            var t = k * step;
            var idx = frameAt(starts, base, t);
            // Never emit the same source frame twice in a row: a duplicate
            // adds bytes and buys nothing. Its share of time is folded into
            // the frame that is already there when delays are diffused below.
            if (idx !== lastIdx) { keep.push(idx); lastIdx = idx; }
        }
        if (keep.length < 2) keep = [0, n - 1];

        // Each kept frame is on screen from its own sample instant until the
        // next one, so its source-time share is the gap between sample times.
        var kept = [];
        for (var kk = 0; kk < keep.length; kk++) {
            var from = starts[keep[kk]];
            var to = kk + 1 < keep.length ? starts[keep[kk + 1]] : baseTotal;
            kept.push(Math.max(1, to - from));
        }

        var outDelays = diffuse(kept, speed, floorCs);
        var achievedCs = sum(outDelays);

        return {
            mode: 'resample',
            speed: speed,
            requestedSpeed: speed,
            achievedSpeed: achievedCs > 0 ? baseTotal / achievedCs : speed,
            delays: outDelays,
            keep: keep,
            frameCount: keep.length,
            sourceFrameCount: n,
            targetDurationMs: Math.round(targetTotalCs * 10),
            achievedDurationMs: achievedCs * 10,
            // Dropping frames from delta-encoded GIFs needs a composite +
            // re-encode, so this path cannot promise byte-level losslessness.
            lossless: false,
            floorClamped: false,
            droppedFrames: n - keep.length
        };
    }

    /**
     * Scale a delay list by `speed` while carrying the rounding remainder
     * forward, so the accumulated duration tracks the exact target instead of
     * drifting by half a centisecond per frame.
     */
    function diffuse(base, speed, floorCs) {
        var out = [];
        var carry = 0;
        for (var i = 0; i < base.length; i++) {
            var exact = base[i] / speed + carry;
            var v = Math.round(exact);
            if (v < floorCs) v = floorCs;
            if (v > MAX_CS) v = MAX_CS;
            // Carry the *unrealised* remainder. When the floor bit, the
            // remainder is negative and the next frames shorten to compensate;
            // clamping it at one floor-width keeps a long clamped run from
            // building a debt that would zero out every later frame.
            carry = exact - v;
            if (carry < -floorCs) carry = -floorCs;
            if (carry > floorCs) carry = floorCs;
            out.push(v);
        }
        return out;
    }

    /** Index of the frame being displayed at source time `t` centiseconds. */
    function frameAt(starts, durs, t) {
        var lo = 0, hi = starts.length - 1;
        while (lo < hi) {
            var mid = (lo + hi + 1) >> 1;
            if (starts[mid] <= t) lo = mid; else hi = mid - 1;
        }
        return lo;
    }

    function sum(a) {
        var s = 0;
        for (var i = 0; i < a.length; i++) s += a[i];
        return s;
    }

    /* ------------------------------------------------------------------ */
    /* applyRetime                                                        */
    /* ------------------------------------------------------------------ */

    /**
     * Rewrite the delay field of every frame in place. Returns a NEW
     * ArrayBuffer; the input is not mutated.
     *
     * Only valid for `mode === 'retime'` plans — a plan that drops frames
     * changes the block structure and must go through decode/encode instead.
     * Throws rather than silently producing a file whose frame count and
     * delay list disagree.
     */
    function applyRetime(buffer, parsed, plan) {
        if (plan.mode !== 'retime') {
            throw new Error('applyRetime: plan drops frames — use the re-encode path');
        }
        if (plan.delays.length !== parsed.frames.length) {
            throw new Error('applyRetime: delay count does not match frame count');
        }

        var src = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
        var out = new Uint8Array(src.length);
        out.set(src);
        var view = new DataView(out.buffer);

        for (var i = 0; i < parsed.frames.length; i++) {
            var f = parsed.frames[i];
            var cs = plan.delays[i];
            if (cs < 0) cs = 0;
            if (cs > MAX_CS) cs = MAX_CS;
            if (f.delayOffset >= 0) {
                view.setUint16(f.delayOffset, cs, true);
            }
            // A frame with no Graphic Control Extension has nowhere to store a
            // delay. Rather than rebuild the container for it, such files are
            // routed to the re-encode path by hasWritableTiming() below.
        }
        return out.buffer;
    }

    /* ------------------------------------------------------------------ */
    /* selectFrames                                                       */
    /* ------------------------------------------------------------------ */

    /**
     * Build a new GIF containing only `keep` (source frame indices, ascending)
     * with `delays` (centiseconds, one per kept frame), by copying each kept
     * frame's raw block verbatim.
     *
     * This is the whole reason the resample path does not cost quality. The
     * obvious implementation of "make it 4x faster past the delay floor" is to
     * decode to RGBA, throw frames away and re-encode, which re-quantises every
     * remaining pixel against a freshly built palette. Here the pixels and the
     * palette are the encoder's original bytes, moved but never recomputed.
     *
     * REQUIRES every frame to cover the full logical screen. In a normal
     * optimised GIF frames are deltas painted over their predecessors, so
     * dropping one leaves the frames after it referencing pixels that were
     * never drawn. Callers must run the file through gifsicle's `-U`
     * (unoptimize) first; `allFramesFull()` below is the guard, and this
     * function throws rather than silently emitting a corrupted animation.
     */
    function selectFrames(buffer, parsed, keep, delays) {
        if (!allFramesFull(parsed)) {
            throw new Error('selectFrames: frames are deltas — unoptimize first');
        }
        if (delays && delays.length !== keep.length) {
            throw new Error('selectFrames: delay count does not match kept frames');
        }
        var src = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);

        var headEnd = parsed.frames[0].blockStart;
        var total = headEnd + 1; // header + trailer
        var i;
        for (i = 0; i < keep.length; i++) {
            var f = parsed.frames[keep[i]];
            total += f.blockEnd - f.blockStart;
        }

        var out = new Uint8Array(total);
        out.set(src.subarray(0, headEnd), 0);
        var at = headEnd;
        var written = [];
        for (i = 0; i < keep.length; i++) {
            var fr = parsed.frames[keep[i]];
            out.set(src.subarray(fr.blockStart, fr.blockEnd), at);
            // Record where this frame's delay field landed so it can be
            // rewritten without a second full parse of the output.
            written.push(fr.delayOffset >= 0 ? at + (fr.delayOffset - fr.blockStart) : -1);
            at += fr.blockEnd - fr.blockStart;
        }
        out[at] = 0x3B; // trailer

        if (delays) {
            var dv = new DataView(out.buffer);
            for (i = 0; i < written.length; i++) {
                if (written[i] >= 0) {
                    var cs = delays[i];
                    if (cs < 0) cs = 0;
                    if (cs > MAX_CS) cs = MAX_CS;
                    dv.setUint16(written[i], cs, true);
                }
            }
        }
        return out.buffer;
    }

    /** Every frame covers the whole logical screen (i.e. file is unoptimized). */
    function allFramesFull(parsed) {
        for (var i = 0; i < parsed.frames.length; i++) {
            if (!parsed.frames[i].full) return false;
        }
        return true;
    }

    /**
     * True when every frame owns a Graphic Control Extension, i.e. the whole
     * file can be retimed by patching bytes. GIF87a files and hand-written
     * exports sometimes omit it for the first frame.
     */
    function hasWritableTiming(parsed) {
        for (var i = 0; i < parsed.frames.length; i++) {
            if (parsed.frames[i].delayOffset < 0) return false;
        }
        return true;
    }

    /* ------------------------------------------------------------------ */
    /* loop count                                                         */
    /* ------------------------------------------------------------------ */

    /**
     * Set the Netscape loop count (0 = forever). Patches in place when the
     * application extension already exists, otherwise splices one in right
     * after the global colour table, where the spec expects it.
     */
    function setLoopCount(buffer, parsed, count) {
        var src = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
        count = Math.max(0, Math.min(MAX_CS, count | 0));

        if (parsed.loopOffset >= 0) {
            var out = new Uint8Array(src.length);
            out.set(src);
            new DataView(out.buffer).setUint16(parsed.loopOffset, count, true);
            return out.buffer;
        }

        var at = 13 + parsed.gctSize * 3;
        var ext = new Uint8Array([
            0x21, 0xFF, 11,
            0x4E, 0x45, 0x54, 0x53, 0x43, 0x41, 0x50, 0x45, 0x32, 0x2E, 0x30, // NETSCAPE2.0
            3, 1, count & 0xFF, (count >> 8) & 0xFF,
            0
        ]);
        var merged = new Uint8Array(src.length + ext.length);
        merged.set(src.subarray(0, at), 0);
        merged.set(ext, at);
        merged.set(src.subarray(at), at + ext.length);
        return merged.buffer;
    }

    /* ------------------------------------------------------------------ */
    /* plays: «play it N times» ↔ the Netscape count                      */
    /* ------------------------------------------------------------------ */

    /**
     * Browsers read the Netscape count as REPEATS AFTER THE FIRST PLAY. Measured
     * 30.10.2026 in Chromium and Firefox on a 2-frame GIF: no block plays once and
     * stops, count 0 loops forever, count 1 plays twice, count 2 three times
     * (docs/why/gif-trim.md; the shared encoder's gifRepeatForPlays measured the
     * same in Chromium 20.10). So a control that says «play once» must write NO
     * block, and «N times» must write N − 1. Every GIF tool that offers a number of
     * plays goes through these three functions.
     */
    /** the file's count → how many times it plays (0 = forever) */
    function playsOf(loopCount) {
        if (loopCount === null || loopCount === undefined) return 1;
        return loopCount === 0 ? 0 : loopCount + 1;
    }
    /** how many times to play (0 = forever) → the count to write; null = no block (plays once) */
    function loopFor(plays) {
        var n = Math.round(Number(plays));
        if (!isFinite(n) || n <= 0) return 0;
        return n === 1 ? null : Math.min(MAX_CS, n - 1);
    }
    /**
     * Make the file play `plays` times (0 = forever): rewrites the count, adds the
     * block, or REMOVES it for a single play. Returns a new ArrayBuffer; callers
     * re-parse — removing the block shifts every offset after it.
     */
    function setPlays(buffer, parsed, plays) {
        var loop = loopFor(plays);
        if (loop !== null) return setLoopCount(buffer, parsed, loop);
        var src = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
        var b = parsed.loopBlock;
        if (!b) return src.slice().buffer;       // no block: it already plays once
        var out = new Uint8Array(src.length - (b[1] - b[0]));
        out.set(src.subarray(0, b[0]), 0);
        out.set(src.subarray(b[1]), b[0]);
        return out.buffer;
    }

    global.GifCore = {
        parse: parse,
        analyze: analyze,
        planSpeed: planSpeed,
        applyRetime: applyRetime,
        selectFrames: selectFrames,
        allFramesFull: allFramesFull,
        hasWritableTiming: hasWritableTiming,
        setLoopCount: setLoopCount,
        setPlays: setPlays,
        playsOf: playsOf,
        loopFor: loopFor,
        BROWSER_FLOOR_CS: BROWSER_FLOOR_CS
    };
})(typeof window !== 'undefined' ? window : globalThis);
